//@ts-nocheck
import Cookies from "js-cookie";
import CryptoJS from 'crypto-js';
import { html } from "lit";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util.js";
import { GlobalVariables, GlobalConstants } from "./constants";
import { getRuntimeConfig } from "./runtime-config";

function getEfmIdentifier(): string {
  return getRuntimeConfig().efmIdentifier;
}
export function stringComparison(originalValue: string, compareValue: string) {
  if (originalValue != null && compareValue != null) {
    return originalValue.toString().toLowerCase() === compareValue.toString().toLowerCase() ? true : false;
  }
  return false;
}
export function stringComparisonCase(originalValue: string, compareValue: string) {
  if (originalValue != null && compareValue != null) {
    return originalValue.toString() === compareValue.toString() ? true : false;
  }
  return false;
}

/** Normalize userType strings that may include spaces or different casing (e.g. "Enterprise Admin"). */
function compactUserTypeKey(decryptedUserType: string): string {
  return (decryptedUserType ?? "").trim().toLowerCase().replace(/\s+/g, "");
}

/**
 * True when decrypted userType is Enterprise Admin.
 */
export function isEnterpriseAdminUserType(decryptedUserType: string): boolean {
  return compactUserTypeKey(decryptedUserType) === "enterpriseadmin";
}

/**
 * True when decrypted userType is Business Unit Admin (not Enterprise Admin).
 * Handles values like "BusinessAdmin", "businessadmin", and "Business Unit Admin".
 * Note: a regex on the substring "businessadmin" fails for spaced forms like "Business Unit Admin".
 */
export function isBusinessUnitAdminUserType(decryptedUserType: string): boolean {
  const compact = compactUserTypeKey(decryptedUserType);
  if (!compact) return false;
  if (compact === "enterpriseadmin") return false;
  return compact === "businessadmin" || compact === "businessunitadmin";
}

/**
 * True for Participant and Credituser (destination) accounts.
 * These users authenticate over SFTP and cannot log in to the JHFT UI, so
 * set-password / set-SSH confirmations should use SFTP messaging instead of Login.
 */
export function isSftpCredentialUserType(userType: string): boolean {
  const compact = compactUserTypeKey(userType);
  return compact === "participant" || compact === "credituser" || compact === "destination";
}

/** Invite and reset links. These pages must show their own shell, not the signed-in app. */
export function isPublicAuthPath(path: string): boolean {
  const normalized = (path || "").split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return normalized === "/login"
    || normalized === "/reset-password"
    || normalized === "/callback"
    || normalized === "/health"
    || normalized.includes("/set-password")
    || normalized.includes("/set-sshkey");
}

/**
 * User type for the set-password / set-SSH confirmation.
 * An SFTP profile wins when the payload also carries the signed-in admin type.
 */
export function credentialConfirmationUserType(response: unknown): string {
  const values: string[] = [];
  const push = (value: unknown, depth: number) => {
    if (depth > 3 || value == null) return;
    if (typeof value === "string" || typeof value === "number") {
      const text = String(value).trim();
      if (text) values.push(text);
      return;
    }
    if (typeof value !== "object") return;
    const record = value as Record<string, unknown>;
    for (const key of ["userType", "UserType", "userProfileType", "UserProfileType", "response", "Response", "data", "Data"]) {
      push(record[key], depth + 1);
    }
  };
  push(response, 0);
  const sftp = values.find((value) => isSftpCredentialUserType(value));
  if (sftp) return sftp;
  return values.find((value) => isEnterpriseAdminUserType(value) || isBusinessUnitAdminUserType(value)) ?? "";
}

function isLocalhostDev(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  );
}

/** ponytail: Intl throws on invalid IANA ids — cheap guard for dev override typos */
function isValidIanaTimeZone(zone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Viewer IANA timezone for display (ACME-1351).
 * On localhost only, VITE_DEV_VIEWER_TIMEZONE in .env.local overrides the browser zone.
 */
export function getCurrentTimezone() {
  const override = String(import.meta.env.VITE_DEV_VIEWER_TIMEZONE ?? "").trim();
  if (override && isLocalhostDev() && isValidIanaTimeZone(override)) {
    return override;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
export function getCurrentOffset() {
  const now = new Date();
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
}
export function getDateByTimeZone(date: string | number) {
  if (date != null && date !== "") {
    const dateInput =
      typeof date === "number" && Number.isFinite(date)
        ? String(Math.floor(date))
        : String(date).trim();
    if (!dateInput) {
      return "";
    }
    let intDate: Date;

    // Handle Unix timestamp (seconds since epoch) - typically 10 digits
    // The C# API returns timestamps as strings like "1769481819"
    if (/^\d{10,13}$/.test(dateInput)) {
      const timestamp = parseInt(dateInput, 10);
      // If it's 10 digits, it's seconds - convert to milliseconds
      // If it's 13 digits, it's already milliseconds
      intDate = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else {
      // Try parsing as a regular date string
      intDate = new Date(dateInput);
    }

    // Check for invalid date
    if (isNaN(intDate.getTime())) {
      console.warn("[getDateByTimeZone] Invalid date value:", dateInput);
      return dateInput; // Return original value if parsing fails
    }

    const formatedDate = new Intl.DateTimeFormat("en-US", {
      timeZone: getCurrentTimezone(),
      timeStyle: "medium",
      dateStyle: "short",
    }).format(intDate);
    return formatedDate;
  }
}
export function getStatusToLowerCase(res: any) {
  const modifiedResults = res.results.map((item: { status: string; }) => ({ ...item, ...{ status: item.status.toLowerCase() } }));
  res.results = modifiedResults;
  return { ...res };
}

/** Shared audit status → jha-badge appearance (reporting dashboard, details, export). */
export const AUDIT_STATUS_BADGE_MAP = [
  ["success", "positive"],
  ["error", "negative"],
  ["skipped", "inverted"],
  ["init", "warning"],
] as const;

/** Advanced Filter options. Values match the audit-search status field. */
export const AUDIT_STATUS_FILTER_OPTIONS = [
  { name: "Success", value: "success" },
  { name: "Error", value: "error" },
  { name: "Skipped", value: "skipped" },
  { name: "Running", value: "init" },
] as const;

/** ponytail: known API statuses use fixed labels; others fall back to generic title-case */
const AUDIT_STATUS_DISPLAY_LABELS: Record<string, string> = {
  success: "Success",
  error: "Error",
  skipped: "Skipped",
  init: "Running",
};

export const STATUS_BADGE_EMPTY_PLACEHOLDER = "—";

/** Full-width block badge inside jha-advanced-table cell buttons (ACME-1756). */
export const STATUS_BADGE_TABLE_STYLE =
  "display:block;width:100%;box-sizing:border-box;text-align:center";

/** Reserve right gutter for STATUS centered header (icon absolute). */
export const ADVANCED_TABLE_SORT_BUTTON_ICON_RESERVE_PX = 28;

/** Min width so STATUS label + sort icon fit without truncating to "S...". */
export const STATUS_COLUMN_MIN_WIDTH_PX = 108;

const SORT_HEADER_HOST_LAYOUT_ATTR = "data-acme-sort-header-layout";
const STATUS_SORT_HEADER_HOST_LAYOUT_ATTR = "data-acme-status-sort-header-layout";

/**
 * Default sortable headers: label shrinks with ellipsis; icon stays in flex flow.
 * ::slotted() only accepts compound selectors — child rules are applied in light DOM
 * via applyDefaultSortButtonLayout() from applyAdvancedTableSortButtonHeaderLayout().
 */
export const ADVANCED_TABLE_SORT_BUTTON_HEADER_HOST_STYLES_TEXT = `
  ::slotted(.sort-button) {
    display: flex !important;
    align-items: center !important;
    width: 100% !important;
    box-sizing: border-box !important;
    gap: 4px !important;
  }
`;

/** STATUS header: center label; pin sort icon outside text flow (children via applyStatusSortButtonLayout). */
export const STATUS_SORT_BUTTON_HEADER_HOST_STYLES_TEXT = `
  ::slotted(.sort-button) {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    width: 100% !important;
    box-sizing: border-box !important;
    padding-right: ${ADVANCED_TABLE_SORT_BUTTON_ICON_RESERVE_PX}px !important;
    gap: 0 !important;
  }
`;

const SORT_BUTTON_ICON_SELECTOR =
  "jh-icon-arrow-up-arrow-down, jh-icon-arrow-up-small";

function applyDefaultSortButtonLayout(sortButton: HTMLElement) {
  sortButton.style.setProperty("display", "flex", "important");
  sortButton.style.setProperty("align-items", "center", "important");
  sortButton.style.setProperty("width", "100%", "important");
  sortButton.style.setProperty("box-sizing", "border-box", "important");
  sortButton.style.setProperty("gap", "4px", "important");
  sortButton.style.removeProperty("position");
  sortButton.style.removeProperty("padding-right");

  const label = sortButton.querySelector("span");
  if (label) {
    const labelEl = label as HTMLElement;
    labelEl.style.setProperty("flex", "1 1 0", "important");
    labelEl.style.setProperty("min-width", "0", "important");
    labelEl.style.setProperty("overflow", "hidden", "important");
    labelEl.style.setProperty("text-overflow", "ellipsis", "important");
    labelEl.style.setProperty("white-space", "nowrap", "important");
    labelEl.style.removeProperty("text-align");
  }

  sortButton.querySelectorAll(SORT_BUTTON_ICON_SELECTOR).forEach((icon) => {
    const iconEl = icon as HTMLElement;
    iconEl.style.setProperty("flex", "0 0 auto", "important");
    iconEl.style.removeProperty("position");
    iconEl.style.removeProperty("right");
    iconEl.style.removeProperty("top");
    iconEl.style.removeProperty("transform");
  });
}

function applyStatusSortButtonLayout(sortButton: HTMLElement) {
  sortButton.style.setProperty("position", "relative", "important");
  sortButton.style.setProperty("display", "flex", "important");
  sortButton.style.setProperty("align-items", "center", "important");
  sortButton.style.setProperty("width", "100%", "important");
  sortButton.style.setProperty("box-sizing", "border-box", "important");
  sortButton.style.setProperty(
    "padding-right",
    `${ADVANCED_TABLE_SORT_BUTTON_ICON_RESERVE_PX}px`,
    "important",
  );
  sortButton.style.setProperty("gap", "0", "important");

  const label = sortButton.querySelector("span");
  if (label) {
    const labelEl = label as HTMLElement;
    labelEl.style.setProperty("flex", "1 1 auto", "important");
    labelEl.style.setProperty("min-width", "0", "important");
    labelEl.style.setProperty("text-align", "center", "important");
    labelEl.style.setProperty("overflow", "visible", "important");
    labelEl.style.setProperty("text-overflow", "clip", "important");
    labelEl.style.setProperty("white-space", "nowrap", "important");
  }

  const descending = sortButton.hasAttribute("descending");
  sortButton.querySelectorAll(SORT_BUTTON_ICON_SELECTOR).forEach((icon) => {
    const iconEl = icon as HTMLElement;
    iconEl.style.setProperty("position", "absolute", "important");
    iconEl.style.setProperty("right", "8px", "important");
    iconEl.style.setProperty("top", "50%", "important");
    iconEl.style.setProperty("flex", "0 0 auto", "important");
    const rotate =
      descending && icon.localName === "jh-icon-arrow-up-small"
        ? "translateY(-50%) rotate(180deg)"
        : "translateY(-50%)";
    iconEl.style.setProperty("transform", rotate, "important");
  });
}

/**
 * Table-shadow fallback (may not pierce jha-table-header light DOM).
 * Prefer ADVANCED_TABLE_SORT_BUTTON_HEADER_HOST_STYLES_TEXT via applyAdvancedTableSortButtonHeaderLayout().
 */
export const ADVANCED_TABLE_SORT_BUTTON_HEADER_STYLES_TEXT = `
  jha-table-header .sort-button {
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;
    width: 100% !important;
    gap: 4px !important;
  }
  jha-table-header .sort-button span {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  jha-table-header .sort-button jh-icon-arrow-up-arrow-down,
  jha-table-header .sort-button jh-icon-arrow-up-small {
    flex: 0 0 auto !important;
  }
`;

function injectHostShadowStyles(
  host: Element,
  attr: string,
  cssText: string,
) {
  const root = (host as HTMLElement).shadowRoot;
  if (!root) return;
  let style = root.querySelector(`style[${attr}]`) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.setAttribute(attr, "");
    root.appendChild(style);
  }
  style.textContent = cssText;
}

/** ponytail: table-shadow CSS may not pierce jha-table-header; inject ::slotted + light-DOM child styles */
export function applyAdvancedTableSortButtonHeaderLayout(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  if (!tableShadowRoot) return;
  tableShadowRoot.querySelectorAll("jha-table-header").forEach((header) => {
    if (header.id === "status") return;
    injectHostShadowStyles(
      header,
      SORT_HEADER_HOST_LAYOUT_ATTR,
      ADVANCED_TABLE_SORT_BUTTON_HEADER_HOST_STYLES_TEXT,
    );
    header.querySelectorAll(".sort-button").forEach((btn) => {
      applyDefaultSortButtonLayout(btn as HTMLElement);
    });
  });
}

/**
 * Center a jha-advanced-table column header when cell content is centered.
 *
 * jha-wc TableColumn only supports `alignRight` (no alignCenter). Sortable headers
 * use `.sort-button { justify-content: space-between }`, which left-aligns the label.
 *
 * Inject this string into the table's shadow root from `injectAdvancedTableStyles()`
 * (same pattern as EFM_TIMEZONE_GRID_TABLE_SHADOW_STYLES_TEXT). Change `#status` to
 * match your column id. Pair with ADVANCED_TABLE_SORT_BUTTON_HEADER_STYLES_TEXT so the
 * sort icon stays pinned right while the label centers in the remaining space.
 */
export const STATUS_COLUMN_HEADER_ADVANCED_TABLE_STYLES_TEXT = `
  jha-table-header#status,
  jha-table-cell#status {
    min-width: ${STATUS_COLUMN_MIN_WIDTH_PX}px;
  }
  jha-table-header#status .sort-button {
    display: flex !important;
    width: 100% !important;
  }
  jha-table-header#status .sort-button span {
    flex: 1 1 auto !important;
    text-align: center !important;
    overflow: visible !important;
    text-overflow: clip !important;
  }
  jha-table-header#status .col-header > span {
    display: block;
    width: 100%;
    text-align: center;
  }
`;

/** ponytail: table-shadow CSS may not pierce jha-table-header; inject ::slotted rules on #status host */
export function applyStatusColumnHeaderLayout(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  if (!tableShadowRoot) return;
  tableShadowRoot.querySelectorAll("jha-table-header#status").forEach((header) => {
    (header as HTMLElement).style.setProperty(
      "min-width",
      `${STATUS_COLUMN_MIN_WIDTH_PX}px`,
      "important",
    );
    injectHostShadowStyles(
      header,
      STATUS_SORT_HEADER_HOST_LAYOUT_ATTR,
      STATUS_SORT_BUTTON_HEADER_HOST_STYLES_TEXT,
    );
    header.querySelectorAll(".sort-button").forEach((btn) => {
      applyStatusSortButtonLayout(btn as HTMLElement);
    });
  });
  tableShadowRoot.querySelectorAll("jha-table-cell#status").forEach((cell) => {
    (cell as HTMLElement).style.setProperty(
      "min-width",
      `${STATUS_COLUMN_MIN_WIDTH_PX}px`,
      "important",
    );
  });
}

const STATUS_CELL_LAYOUT_ATTR = "data-acme-1756-status-cell";

const STATUS_CELL_HOST_SHADOW_STYLES_TEXT = `
  ::slotted(button[data-button]) {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
    box-sizing: border-box !important;
    text-align: center !important;
  }
  ::slotted(jha-badge),
  ::slotted(jha-tooltip) {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
    text-align: center !important;
  }
`;

function injectStatusCellHostShadowStyles(host: Element) {
  const root = host.shadowRoot;
  if (!root) return;
  let style = root.querySelector(
    `style[${STATUS_CELL_LAYOUT_ATTR}]`,
  ) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.setAttribute(STATUS_CELL_LAYOUT_ATTR, "");
    root.appendChild(style);
  }
  style.textContent = STATUS_CELL_HOST_SHADOW_STYLES_TEXT;
}

function applyStatusCellLayout(cell: Element) {
  injectStatusCellHostShadowStyles(cell);
  const button = cell.querySelector("button[data-button]") as HTMLElement | null;
  if (button) {
    button.style.setProperty("display", "flex", "important");
    button.style.setProperty("justify-content", "center", "important");
    button.style.setProperty("align-items", "center", "important");
    button.style.setProperty("width", "100%", "important");
    button.style.setProperty("box-sizing", "border-box", "important");
    button.style.setProperty("text-align", "center", "important");
  }
  cell.querySelectorAll("jha-badge, jha-tooltip").forEach((el) => {
    const node = el as HTMLElement;
    node.style.setProperty("display", "block", "important");
    node.style.setProperty("width", "100%", "important");
    node.style.setProperty("box-sizing", "border-box", "important");
    node.style.setProperty("text-align", "center", "important");
  });
}

/** All sortable jha-advanced-table headers: icon gutter + label ellipsis. */
export function applyAdvancedTableHeaderLayout(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  applyAdvancedTableSortButtonHeaderLayout(tableShadowRoot);
}

/** Reporting STATUS column: all headers + centered status header/cells. */
export function applyAuditReportingTableLayout(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  applyAdvancedTableHeaderLayout(tableShadowRoot);
  applyStatusColumnHeaderLayout(tableShadowRoot);
  applyStatusColumnCellLayout(tableShadowRoot);
}

/** @deprecated Use applyAuditReportingTableLayout */
export function applyStatusColumnTableLayout(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  applyAuditReportingTableLayout(tableShadowRoot);
}

/** Audit dashboard columns that clip text — keep native title on inner ellipsis element only. */
export const AUDIT_DASHBOARD_ELLIPSIS_TITLE_COLUMN_IDS = new Set([
  "sourceFilePath",
  "destinationFileServer",
]);

/** Audit details columns that clip text — keep native title on inner ellipsis element only. */
export const AUDIT_DETAILS_ELLIPSIS_TITLE_COLUMN_IDS = new Set([
  "logMessage",
  "sourceFilePath",
  "destinationFilePath",
  "fileName",
]);

/** Shared ellipsis cell class for jha-advanced-table valueRenderer + shadow CSS. */
export const ADVANCED_TABLE_ELLIPSIS_CELL_CLASS = "acme-grid-cell-ellipsis";

export const ADVANCED_TABLE_ELLIPSIS_CELL_INLINE_STYLE =
  "white-space: nowrap; overflow: hidden; text-overflow: ellipsis;";

/** Ellipsis cell with native title for truncated long values (not jha-tooltip). */
export function renderAdvancedTableEllipsisCell(
  val: unknown,
  { withTitle = true, className = ADVANCED_TABLE_ELLIPSIS_CELL_CLASS } = {},
) {
  const s = val == null ? "" : String(val);
  return withTitle
    ? html`<div class=${className} title=${s}>${s}</div>`
    : html`<div class=${className}>${s}</div>`;
}

/**
 * Shadow CSS: max-width + ellipsis on long-text columns (inject into jha-advanced-table).
 * Pair with renderAdvancedTableEllipsisCell and stripAdvancedTableRedundantTitles allowlist.
 */
export function buildAdvancedTableEllipsisColumnShadowCss(
  columnIds: readonly string[],
  cellClass = ADVANCED_TABLE_ELLIPSIS_CELL_CLASS,
): string {
  if (!columnIds.length) return "";
  const cells = columnIds
    .map(
      (id) =>
        `jha-table-row[expand-all-columns] jha-table-cell#${id}[custom-value]:not([checkbox]), jha-table-row[expand-all-columns] jha-table-cell#${id}:not([checkbox])`,
    )
    .join(",\n        ");
  const buttons = columnIds
    .map(
      (id) =>
        `jha-table-row[expand-all-columns] jha-table-cell#${id}[custom-value] button[data-button], jha-table-row[expand-all-columns] jha-table-cell#${id} button[data-button]`,
    )
    .join(",\n        ");
  const inner = columnIds
    .map(
      (id) =>
        `jha-table-row[expand-all-columns] jha-table-cell#${id}[custom-value] .${cellClass}, jha-table-row[expand-all-columns] jha-table-cell#${id} .${cellClass}`,
    )
    .join(",\n        ");
  return `
        ${cells} {
            overflow: hidden !important;
            max-width: var(--jha-advanced-table-column-max-width, 250px);
            min-width: 120px;
        }
        ${buttons} {
            overflow: hidden !important;
            max-width: 100% !important;
            min-width: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
            display: block !important;
            white-space: normal !important;
        }
        ${inner} {
            display: block !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
        }`;
}

/**
 * jha-tooltip ::after renders outside the cell; overflow:hidden on table cells clips it.
 * Hover/focus-within lifts z-index and overflow:visible only while the tooltip is active.
 */
export function buildAdvancedTableJhaTooltipOverflowEscapeCss(
  columnIds: readonly string[],
): string {
  if (!columnIds.length) return "";
  const base = columnIds
    .flatMap((id) => [
      `jha-table-cell[custom-value]#${id}`,
      `jha-table-row[expand-all-columns] jha-table-cell#${id}:not([checkbox])`,
    ])
    .join(",\n      ");
  const hover = columnIds
    .flatMap((id) => [
      `jha-table-cell[custom-value]#${id}:hover`,
      `jha-table-cell[custom-value]#${id}:focus-within`,
      `jha-table-row[expand-all-columns] jha-table-cell#${id}:not([checkbox]):hover`,
      `jha-table-row[expand-all-columns] jha-table-cell#${id}:not([checkbox]):focus-within`,
    ])
    .join(",\n      ");
  const buttons = columnIds
    .flatMap((id) => [
      `jha-table-cell[custom-value]#${id} button[data-button]`,
      `jha-table-row[expand-all-columns] jha-table-cell#${id} button[data-button]`,
    ])
    .join(",\n      ");
  const hoverButtons = columnIds
    .flatMap((id) => [
      `jha-table-cell[custom-value]#${id}:hover button[data-button]`,
      `jha-table-cell[custom-value]#${id}:focus-within button[data-button]`,
      `jha-table-row[expand-all-columns] jha-table-cell#${id}:hover button[data-button]`,
      `jha-table-row[expand-all-columns] jha-table-cell#${id}:focus-within button[data-button]`,
    ])
    .join(",\n      ");
  return `
      ${base} {
        overflow: hidden !important;
      }
      ${hover} {
        z-index: 10;
        overflow: visible !important;
      }
      ${buttons} {
        overflow: hidden !important;
        max-width: 100%;
        min-width: 0;
      }
      ${hoverButtons} {
        overflow: visible !important;
      }`;
}

/**
 * jha-advanced-table sets title on every cell button from getColValueString.
 * With dataType "number" that is locale-formatted epoch seconds (e.g. 1,763,584,783).
 * Strip redundant native titles; ellipsis columns keep title on their inner element.
 * Columns in preserveInnerTitleColumnIds keep intentional inner titles (e.g. action menus).
 *
 * ponytail: second pass on the next animation frame — jha-wc can re-apply button title after paint.
 */
export function stripAdvancedTableRedundantTitles(
  tableShadowRoot: ShadowRoot | null | undefined,
  ellipsisTitleColumnIds: ReadonlySet<string>,
  preserveInnerTitleColumnIds: ReadonlySet<string> = new Set(),
) {
  if (!tableShadowRoot) return;
  const stripOnce = () => {
    tableShadowRoot.querySelectorAll("jha-table-cell.button-cell").forEach((cell) => {
      const colId = cell.id;
      if (!colId) return;
      const preserveInnerTitles =
        ellipsisTitleColumnIds.has(colId) || preserveInnerTitleColumnIds.has(colId);
      const btn = cell.querySelector("button[data-button]");
      btn?.removeAttribute("title");
      if (preserveInnerTitles) {
        return;
      }
      cell.removeAttribute("title");
      cell.querySelectorAll("[title]").forEach((el) => {
        if (el !== btn) {
          el.removeAttribute("title");
        }
      });
    });
  };
  stripOnce();
  requestAnimationFrame(stripOnce);
}

/** Full-width centered STATUS badges after jha-advanced-table renders cells. */
export function applyStatusColumnCellLayout(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  if (!tableShadowRoot) return;
  tableShadowRoot.querySelectorAll("jha-table-cell#status").forEach(applyStatusCellLayout);
}

/** Display-only Title Case for status badge labels; does not change row/filter data. */
export function formatStatusBadgeLabel(status: string | null | undefined): string {
  const key = String(status ?? "").trim().toLowerCase();
  if (!key) return "";
  if (AUDIT_STATUS_DISPLAY_LABELS[key]) {
    return AUDIT_STATUS_DISPLAY_LABELS[key];
  }
  return key
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Resolve jha-advanced-table badge appearance from raw status + badgeMap keys. */
export function getStatusBadgeAppearance(
  status: string | null | undefined,
  badgeMap: ReadonlyArray<readonly [string, string]> = AUDIT_STATUS_BADGE_MAP,
): string {
  const key = String(status ?? "").trim().toLowerCase();
  if (!key) return "neutral";
  const match = badgeMap.find(([mapKey]) => mapKey === key);
  if (!match) {
    console.warn(
      `[status-badge] unmapped status "${String(status).trim()}"; using neutral appearance`,
    );
    return "neutral";
  }
  return match[1];
}

/** Render colored status badge with Title Case label; keeps badgeMap keyed on raw status. */
export function renderStatusBadge(
  status: string | null | undefined,
  badgeMap: ReadonlyArray<readonly [string, string]> = AUDIT_STATUS_BADGE_MAP,
) {
  const key = String(status ?? "").trim().toLowerCase();
  if (!key) {
    return html`<span>${STATUS_BADGE_EMPTY_PLACEHOLDER}</span>`;
  }
  const appearance = getStatusBadgeAppearance(key, badgeMap);
  return html`<jha-badge appearance=${appearance} style=${STATUS_BADGE_TABLE_STYLE}>${formatStatusBadgeLabel(key)}</jha-badge>`;
}

/** Audit details STATUS cell: Success/Error/Skipped as badges; init (step start) left blank to avoid implying the step is still running. */
export function renderAuditDetailsStatusCell(status: string | null | undefined) {
  const key = String(status ?? "").trim().toLowerCase();
  if (!key || key === "init") {
    return html`<span>${STATUS_BADGE_EMPTY_PLACEHOLDER}</span>`;
  }
  return html`<jha-tooltip text="Click To Copy" bottom style=${STATUS_BADGE_TABLE_STYLE}>${renderStatusBadge(status)}</jha-tooltip>`;
}
/** Token expiry only. Do not stamp lastActiveTime here — API calls would defeat idle logout (CWE-613). */
export function isSessionExpired() {
  const expiryTime = Cookies.get("expiryDateTime");
  if (expiryTime && new Date() > new Date(expiryTime)) {
    return true;
  }
  return false;
}

/** True when last user activity is older than maxMs (missing/invalid stamp counts as idle). */
export function isIdlePastMax(
  lastActiveIso: string | undefined | null,
  nowMs: number,
  maxMs: number
) {
  if (!lastActiveIso) {
    return true;
  }
  const last = Date.parse(lastActiveIso);
  if (Number.isNaN(last)) {
    return true;
  }
  return nowMs - last > maxMs;
}

/** Parsed last-activity epoch, or null when the cookie is missing/unparseable. */
export function parseLastActiveMs(lastActiveIso: string | undefined | null): number | null {
  if (!lastActiveIso) {
    return null;
  }
  const last = Date.parse(lastActiveIso);
  return Number.isNaN(last) ? null : last;
}

/**
 * Remaining idle-window milliseconds. Missing/invalid stamp returns the full window
 * so a bad cookie cannot schedule an immediate logout.
 */
export function remainingIdleMs(
  lastActiveIso: string | undefined | null,
  nowMs: number,
  maxMs: number
): number {
  const last = parseLastActiveMs(lastActiveIso);
  if (last == null) {
    return maxMs;
  }
  const remaining = maxMs - (nowMs - last);
  return remaining > 0 ? remaining : 0;
}

/**
 * True only when a parseable activity stamp exists AND it is already past the idle window.
 *
 * Gate every write of `lastActiveTime` on this. Stamping unconditionally lets a late
 * activity signal (window focus, a cross-tab broadcast, app startup after a refresh)
 * overwrite an expired stamp and revive a session that should have ended (CWE-613).
 *
 * A missing or unparseable stamp reads as `false` on purpose: `isIdlePastMax` treats it as
 * idle, and blocking on that would log users out at sign-in, before a first stamp exists.
 */
export function hasExpiredIdleStamp(
  lastActiveIso: string | undefined | null,
  nowMs: number,
  maxMs: number
): boolean {
  return parseLastActiveMs(lastActiveIso) != null && isIdlePastMax(lastActiveIso, nowMs, maxMs);
}

/**
 * Delay until token refresh. `null` = missing/invalid expiry (do not schedule).
 * `0` = refresh immediately (due now or already past — one-shot).
 * A ~13-minute access token yields ~8 minutes (expiry minus the 5-minute lead).
 */
export function sessionRefreshDelayMs(
  expiryIso: string | undefined | null,
  nowMs: number,
  refreshBeforeExpiryMs: number
): number | null {
  if (!expiryIso) {
    return null;
  }
  const expiryMs = Date.parse(expiryIso);
  if (Number.isNaN(expiryMs)) {
    return null;
  }
  const delay = expiryMs - refreshBeforeExpiryMs - nowMs;
  return delay > 0 ? delay : 0;
}

export type SessionRefreshOutcome = "renew" | "idle" | "expired" | "renewal-failed";

/** Decide refresh-timer action. A failed refresh is never treated as inactivity. */
export function classifySessionRefreshDue(args: {
  tokenExpired: boolean;
  remainingIdleMs: number;
  refreshSucceeded?: boolean;
}): SessionRefreshOutcome {
  if (args.tokenExpired) {
    return "expired";
  }
  if (args.remainingIdleMs <= 0) {
    return "idle";
  }
  if (args.refreshSucceeded === false) {
    return "renewal-failed";
  }
  return "renew";
}

/**
 * postRequest returns a raw Response for HTTP 401/404 (body not parsed).
 * Callers that need `details` / `messageStatus` must normalize here.
 */
export async function readPostRequestPayload(result: unknown): Promise<any> {
  if (result == null) {
    return null;
  }
  if (typeof Response !== "undefined" && result instanceof Response) {
    try {
      return await result.json();
    } catch {
      return null;
    }
  }
  return result;
}
export function millisToMinutesAndSeconds(millis: number) {
  let minutes = Math.floor(millis / 60000);
  let seconds = ((millis % 60000) / 1000).toFixed(0) as unknown as number;
  return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
}
export function encryptValue(value: string): string {
  try {
    if (!value || !getEfmIdentifier()) {
      throw new Error("Missing value or identifier");
    }

    return CryptoJS.AES.encrypt(value, getEfmIdentifier()).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    return "";
  }
}
export function decryptValue(encryptedValue: string): string {
  try {
    if (!encryptedValue || !getEfmIdentifier()) {
      return "";
    }
    const bytes = CryptoJS.AES.decrypt(encryptedValue, getEfmIdentifier());
    const decryptedValue = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedValue) {
      return "";
    }
    return decryptedValue;
  } catch (error) {
    console.error("Error decrypting value:", error);
    return "";
  }
}
export function showToastMessage(message: string, role: string) {
  showToast({
    message: message,
    role: role,
    duration: GlobalVariables.TOAST_TIMEOUT,
  });
}
export function getCurrentDateTimeInTimeZone(timezone: string): Date {
  const today = new Date();
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(today);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
}
export function compareUTCDateTime(startDateTime: Date, endDateTime: Date) {
  let now = getCurrentDateTimeInTimeZone("UTC");
  const backDate = new Date(now);
  backDate.setMonth(backDate.getMonth() - GlobalConstants.CALANDER_MIN_DATE_MONTHS);
  let startUTC = new Date(Date.UTC(startDateTime.getUTCFullYear(), startDateTime.getUTCMonth(), startDateTime.getUTCDate(),
    startDateTime.getUTCHours(), startDateTime.getUTCMinutes(), startDateTime.getUTCSeconds()));
  let endUTC = new Date(Date.UTC(endDateTime.getUTCFullYear(), endDateTime.getUTCMonth(), endDateTime.getUTCDate(),
    endDateTime.getUTCHours(), endDateTime.getUTCMinutes(), endDateTime.getUTCSeconds()));

  function formatDateTime(date: Date) {
    return date.getUTCFullYear() + "-" +
      String(date.getMonth() + 1).padStart(2, "0") + "-" +
      String(date.getDate()).padStart(2, "0") + " " +
      String(date.getHours()).padStart(2, "0") + ":" +
      String(date.getMinutes()).padStart(2, "0");
  }


  if (startUTC > now) {
    return `The start date/time cannot be in the future. Current UTC time: ${formatDateTime(now)}.`;
  }
  if (endUTC > now) {
    return `The end date/time cannot be in the future. Current UTC time: ${formatDateTime(now)}.`;
  }
  if (startUTC < backDate) {
    return `The start date/time cannot be earlier than ${GlobalConstants.CALANDER_MIN_DATE_MONTHS} months: ${formatDateTime(backDate)}.`;
  }
  if (endUTC < backDate) {
    return `The end date/time cannot be earlier than ${GlobalConstants.CALANDER_MIN_DATE_MONTHS} months: ${formatDateTime(backDate)}.`;
  }

  return "";
}
export function compareCurrentTimeWithStartEndDateTime(startDateTime: Date, endDateTime: Date, timeZone: string): string {
  const now = getCurrentDateTimeInTimeZone(timeZone);
  const backDate = new Date(now);
  backDate.setMonth(backDate.getMonth() - GlobalConstants.CALANDER_MIN_DATE_MONTHS);

  function formatDateTime(date: Date) {
    return date.getFullYear() + "-" +
      String(date.getMonth() + 1).padStart(2, "0") + "-" +
      String(date.getDate()).padStart(2, "0") + " " +
      String(date.getHours()).padStart(2, "0") + ":" +
      String(date.getMinutes()).padStart(2, "0");
  }

  if (startDateTime > now) {
    return `The start date/time cannot be in the future. Current time: ${formatDateTime(now)}.`;
  }
  if (endDateTime > now) {
    return `The end date/time cannot be in the future. Current time: ${formatDateTime(now)}.`;
  }
  if (startDateTime < backDate) {
    return `The start date/time cannot be earlier than ${GlobalConstants.CALANDER_MIN_DATE_MONTHS} months: ${formatDateTime(backDate)}.`;
  }
  if (endDateTime < backDate) {
    return `The end date/time cannot be earlier than ${GlobalConstants.CALANDER_MIN_DATE_MONTHS} months: ${formatDateTime(backDate)}.`;
  }

  return "";
}
export function getTimeZoneIdentifier(zone) {
  const timezoneMapping = [
    { value: "us_central", name: "America/Chicago" },
    { value: "us_eastern", name: "America/New_York" },
    { value: "us_pacific", name: "America/Los_Angeles" },
    { value: "us_mountain", name: "America/Denver" },
    { value: "us_alaska", name: "America/Anchorage" }
  ];
  const timezone = timezoneMapping.find(y => y.value === zone.toLowerCase());
  if (timezone) {
    return timezone.name;
  } else {
    return "";
  }
}
export function formatToUTC(date: Date): string {
  return date + " UTC";
}

// ============================================================================
// Environment-aware API URL — zero impact on deployed environments
// ============================================================================
// Returns the API URL appropriate for how the app is running (Vite env + hostname).
// On localhost, may redirect certain URLs to the remote reference env when
// VITE_USE_DEV_REFERENCE_DATA is set (see .env.local); on deployed hosts, returns
// apiUrl unchanged.
//
// This is a no-op outside local dev due to two independent safeguards:
//   1. VITE_USE_DEV_REFERENCE_DATA must be "true" in the built bundle (typically only
//      when you build/serve locally with those vars — not a concern for prod/test/staging).
//   2. window.location.hostname must be "localhost" (browser-enforced; end users on
//      deployed hosts never match).
//
// This is not a production security feature; it is convenience for local debugging.
// ============================================================================
export function getEnvApiUrl(apiUrl: string): string {
  const useDevReferenceData = import.meta.env.VITE_USE_DEV_REFERENCE_DATA === "true";

  if (!useDevReferenceData) {
    return apiUrl;
  }

  // Only redirect when on localhost
  if (window.location.hostname !== "localhost") {
    return apiUrl;
  }

  // Replace local API base with dev server
  const devApiBase = import.meta.env.VITE_DEV_REFERENCE_DATA_URL;
  const localApiBase = import.meta.env.VITE_API_BASE_URL;

  if (!devApiBase || !localApiBase) {
    return apiUrl;
  }

  return apiUrl.replace(localApiBase, devApiBase);
}
